import React, { useMemo, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { useIntl } from 'react-intl';

export interface WorkoutOption {
  id: number;
  planName: string;
}

export interface WeekGridAssignment {
  workoutId: number;
  dayOfWeek: number;
}

interface WeekGridAssignProps {
  workouts: WorkoutOption[];
  durationInWeeks: number;
  startDate: Date | null;
  assignments: WeekGridAssignment[];
  onAssign: (dayOfWeek: number, workoutId: number) => void;
  onUnassign: (dayOfWeek: number) => void;
  replicate?: boolean;
  onReplicateChange?: (next: boolean) => void;
  showReplicateToggle?: boolean;
  /** Controlled selected-week (1-based). When provided, the component delegates week tracking to the parent. */
  selectedWeek?: number;
  onWeekChange?: (week: number) => void;
  /** Pre-existing assignments (e.g. already saved on the cycle). Rendered like normal pills but locked (no X). */
  lockedAssignments?: WeekGridAssignment[];
  /**
   * Modo lectura (#39): oculta la columna de sesiones disponibles, el toggle de
   * replicar, los botones de quitar y los placeholders de "Soltar aquí". Deja
   * solo la grilla de la semana, la navegación entre semanas y el resumen, que
   * es lo que se reutiliza al hacer click en "Ver" un ciclo.
   */
  readOnly?: boolean;
}

const DAY_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const TONES = ['primary', 'success', 'warning', 'danger'] as const;
type Tone = (typeof TONES)[number];

function toneForWorkout(id: number): Tone {
  return TONES[id % TONES.length] as Tone;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function DraggableSession({ workout, tone }: { workout: WorkoutOption; tone: Tone }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `workout-${workout.id}`,
    data: { workoutId: workout.id }
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`et-week-grid__session-card${isDragging ? ' is-dragging' : ''}`}
      data-tone={tone}
    >
      <i className="pi pi-bars et-week-grid__session-grip" />
      <span className="et-week-grid__session-stripe" aria-hidden />
      <div className="et-week-grid__session-meta">
        <div className="et-week-grid__session-name">{workout.planName}</div>
      </div>
    </div>
  );
}

function DayCell({
  dayLabel,
  date,
  workout,
  tone,
  isRest,
  isDropping,
  dayOfWeek,
  onRemove,
  locked,
  readOnly = false
}: {
  dayLabel: string;
  date: Date | null;
  workout: WorkoutOption | null;
  tone: Tone;
  isRest: boolean;
  isDropping: boolean;
  dayOfWeek: number;
  onRemove: () => void;
  locked?: boolean;
  readOnly?: boolean;
}) {
  const { isOver, setNodeRef } = useDroppable({
    id: `day-${dayOfWeek}`,
    data: { dayOfWeek }
  });

  const className = [
    'et-week-grid__day',
    workout ? 'is-filled' : '',
    isRest ? 'is-rest' : '',
    isOver ? 'is-dropping' : '',
    isDropping ? 'is-dropping' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={setNodeRef} className={className} data-tone={workout ? tone : undefined}>
      <div className="et-week-grid__day-head">
        <span className="et-week-grid__day-label">{dayLabel}</span>
        {date && <span className="et-week-grid__day-date">{date.getDate()}</span>}
      </div>
      {workout ? (
        <div className={`et-week-grid__day-pill${locked || readOnly ? ' is-locked' : ''}`}>
          <span className="et-week-grid__day-pill-name">{workout.planName}</span>
          {locked || readOnly ? (
            !readOnly && <i className="pi pi-lock et-week-grid__day-pill-lock" title="Ya asignado al ciclo" />
          ) : (
            <button
              type="button"
              className="et-week-grid__day-pill-remove"
              aria-label="Quitar"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
            >
              <i className="pi pi-times" />
            </button>
          )}
        </div>
      ) : isRest ? (
        <div className="et-week-grid__day-rest">
          <span className="et-week-grid__day-rest-icon">Z</span>
          <span className="et-week-grid__day-rest-label">Descanso</span>
        </div>
      ) : readOnly ? (
        // Sin invitación a soltar: en modo lectura un día vacío es solo un día
        // sin entrenamiento.
        <div className="et-week-grid__day-empty" />
      ) : (
        <div className="et-week-grid__day-drop">
          <i className="pi pi-plus-circle" />
          <span>Soltar aquí</span>
        </div>
      )}
    </div>
  );
}

/**
 * v2 — Week-grid assignment UI (design handoff §4.2 step 2 and §05 calendar
 * assign dialog). Drag a workout template from the sidebar onto a day of the
 * week to schedule it. By default, the layout shows week 1; the week-N
 * navigator lets the coach preview/edit other weeks. When `replicate` is on,
 * assignments apply to all weeks of the cycle (the default behavior).
 *
 * The component is controlled — `assignments` and `onAssign/onUnassign` come
 * from the parent (typically the wizard hook).
 */
const WeekGridAssign: React.FC<WeekGridAssignProps> = ({
  workouts,
  durationInWeeks,
  startDate,
  assignments,
  onAssign,
  onUnassign,
  replicate = true,
  onReplicateChange,
  showReplicateToggle = true,
  selectedWeek: controlledWeek,
  onWeekChange,
  lockedAssignments,
  readOnly = false
}) => {
  const intl = useIntl();
  const [search, setSearch] = useState('');
  const [internalWeek, setInternalWeek] = useState(1);
  const selectedWeek = controlledWeek ?? internalWeek;
  const setSelectedWeek = (n: number) => {
    if (onWeekChange) onWeekChange(n);
    if (controlledWeek == null) setInternalWeek(n);
  };
  const [activeId, setActiveId] = useState<number | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const activeWorkout = activeId != null ? (workouts.find((w) => w.id === activeId) ?? null) : null;

  const filteredWorkouts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return workouts;
    return workouts.filter((w) => w.planName.toLowerCase().includes(q));
  }, [workouts, search]);

  const weekStart = useMemo(() => {
    if (!startDate) return null;
    return addDays(startDate, (selectedWeek - 1) * 7);
  }, [startDate, selectedWeek]);

  const assignmentsByDay = useMemo(() => {
    const map = new Map<number, WeekGridAssignment & { locked?: boolean }>();
    // Locked first so user-added drops can visually replace them in the same
    // cell, but the unlocked entry remains the source of truth for onUnassign.
    (lockedAssignments ?? []).forEach((a) => {
      if (a.dayOfWeek != null && a.workoutId != null) map.set(a.dayOfWeek, { ...a, locked: true });
    });
    assignments.forEach((a) => {
      if (a.dayOfWeek != null && a.workoutId != null) map.set(a.dayOfWeek, { ...a, locked: false });
    });
    return map;
  }, [assignments, lockedAssignments]);

  const handleDragStart = (event: DragStartEvent) => {
    const workoutId = event.active.data.current?.workoutId as number | undefined;
    if (workoutId != null) setActiveId(workoutId);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const dayId = event.over?.id;
    const workoutId = event.active.data.current?.workoutId as number | undefined;
    if (!dayId || typeof dayId !== 'string' || !workoutId) return;
    const dayOfWeek = parseInt(dayId.replace('day-', ''), 10);
    if (!Number.isFinite(dayOfWeek)) return;
    onAssign(dayOfWeek, workoutId);
  };

  const handleDragCancel = () => setActiveId(null);

  const sessionsPerWeek = assignmentsByDay.size;
  const totalSessions = sessionsPerWeek * (durationInWeeks || 1);
  const restDays = 7 - sessionsPerWeek;
  const weekRangeLabel = useMemo(() => {
    if (!weekStart) return null;
    const end = addDays(weekStart, 6);
    const fmt = (d: Date) => `${d.getDate()}/${d.getMonth() + 1}`;
    return `${fmt(weekStart)} – ${fmt(end)}`;
  }, [weekStart]);

  const weekChips = useMemo(() => {
    const n = Math.max(durationInWeeks || 1, 1);
    return Array.from({ length: n }, (_, i) => i + 1);
  }, [durationInWeeks]);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className={`et-week-grid${readOnly ? ' is-readonly' : ''}`}>
        {!readOnly && (
          <aside className="et-week-grid__sidebar">
            <label className="et-week-grid__label">
              {intl.formatMessage({
                id: 'weekGrid.availableSessions',
                defaultMessage: 'Sesiones disponibles'
              })}
            </label>
            <div className="et-week-grid__sidebar-list">
              <InputText
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={intl.formatMessage({ id: 'common.search', defaultMessage: 'Buscar...' })}
                className="p-inputtext-sm w-full et-week-grid__search"
              />
              {filteredWorkouts.length === 0 ? (
                <div className="et-week-grid__empty-list">
                  {intl.formatMessage({
                    id: 'weekGrid.noWorkouts',
                    defaultMessage: 'No hay sesiones disponibles'
                  })}
                </div>
              ) : (
                filteredWorkouts.map((w) => <DraggableSession key={w.id} workout={w} tone={toneForWorkout(w.id)} />)
              )}
            </div>

            {showReplicateToggle && durationInWeeks > 1 && (
              <div className="et-week-grid__replicate">
                <Checkbox
                  inputId="weekGridReplicate"
                  checked={replicate}
                  onChange={(e) => onReplicateChange?.(!!e.checked)}
                />
                <label htmlFor="weekGridReplicate" className="et-week-grid__replicate-body">
                  <div className="et-week-grid__replicate-title">
                    {intl.formatMessage(
                      {
                        id: 'weekGrid.replicateToAll',
                        defaultMessage: 'Replicar a las {n} semanas'
                      },
                      { n: durationInWeeks }
                    )}
                  </div>
                  <div className="et-week-grid__replicate-desc">
                    {intl.formatMessage({
                      id: 'weekGrid.replicateDesc',
                      defaultMessage: 'Lo que armás en la semana 1 se copia al resto.'
                    })}
                  </div>
                </label>
              </div>
            )}
          </aside>
        )}

        <div className="et-week-grid__main">
          <div className="et-week-grid__weeks">
            <label className="et-week-grid__label" style={{ marginBottom: 0 }}>
              {intl.formatMessage({ id: 'weekGrid.week', defaultMessage: 'Semana' })}
            </label>
            <div className="et-week-grid__week-chips" role="tablist">
              {weekChips.map((n) => (
                <button
                  key={n}
                  type="button"
                  role="tab"
                  aria-selected={n === selectedWeek}
                  className={`et-week-grid__week-chip${n === selectedWeek ? ' is-active' : ''}`}
                  onClick={() => setSelectedWeek(n)}
                >
                  {n}
                </button>
              ))}
            </div>
            {weekRangeLabel && <span className="et-week-grid__week-range">{weekRangeLabel}</span>}
          </div>

          <div className="et-week-grid__days">
            {DAY_LABELS.map((label, i) => {
              const dow = i + 1; // 1=Lun, 7=Dom
              const assignment = assignmentsByDay.get(dow) ?? null;
              const workout = assignment ? (workouts.find((w) => w.id === assignment.workoutId) ?? null) : null;
              const date = weekStart ? addDays(weekStart, i) : null;
              const tone = workout ? toneForWorkout(workout.id) : 'primary';
              const isRest = dow === 7 && !workout;
              return (
                <DayCell
                  key={dow}
                  dayLabel={label}
                  date={date}
                  workout={workout}
                  tone={tone}
                  isRest={isRest}
                  isDropping={false}
                  dayOfWeek={dow}
                  onRemove={() => onUnassign(dow)}
                  locked={assignment?.locked}
                  readOnly={readOnly}
                />
              );
            })}
          </div>

          <div className="et-week-grid__summary">
            <SumStat
              n={sessionsPerWeek}
              label={intl.formatMessage({ id: 'weekGrid.sessionsPerWeek', defaultMessage: 'sesiones/semana' })}
            />
            <SumStat
              n={totalSessions}
              label={intl.formatMessage({ id: 'weekGrid.totalSessions', defaultMessage: 'total de sesiones' })}
              mono
            />
            <SumStat
              n={restDays}
              label={intl.formatMessage({ id: 'weekGrid.restDays', defaultMessage: 'días de descanso' })}
            />
          </div>
        </div>
      </div>
      <DragOverlay zIndex={9999} dropAnimation={null}>
        {activeWorkout ? (
          <div className="et-week-grid__session-card is-overlay" data-tone={toneForWorkout(activeWorkout.id)}>
            <i className="pi pi-bars et-week-grid__session-grip" />
            <span className="et-week-grid__session-stripe" aria-hidden />
            <div className="et-week-grid__session-meta">
              <div className="et-week-grid__session-name">{activeWorkout.planName}</div>
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

function SumStat({ n, label, mono }: { n: number; label: string; mono?: boolean }) {
  return (
    <div className="et-week-grid__stat">
      <span className={`et-week-grid__stat-value${mono ? ' is-mono' : ''}`}>{n}</span>
      <span className="et-week-grid__stat-label">{label}</span>
    </div>
  );
}

export default WeekGridAssign;
